"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.restSettings = void 0;
/* eslint-disable max-len */
const params = {
    // Id of a Correlation.
    correlationId: ':correlation_id',
    // Instance Id of an EmptyActivity.
    emptyActivityInstanceId: ':empty_activity_instance_id',
    // Id of an Event.
    eventId: ':event_id',
    // Name of an Event.
    eventName: ':event_name',
    // Id of an External Task.
    externalTaskId: ':external_task_id',
    // Id of a FlowNode.
    flowNodeId: ':flow_node_id',
    // Id of a FlowNodeInstance.
    flowNodeInstanceId: ':flow_node_instance_id',
    // Id of a ManualTask.
    manualTaskInstanceId: ':manual_task_instance_id',
    // Instance Id of a Process.
    processInstanceId: ':process_instance_id',
    // Id of a ProcessModel.
    processDefinitionId: ':process_definition_id',
    // Id of a ProcessModel.
    processModelId: ':process_model_id',
    // Id of a UserTaskInstance.
    userTaskInstanceId: ':user_task_instance_id',
};
const paths = {
    // ApplicationInfo
    /*
     * Gets some basic info about the host application.
     * @tags ApplicationInfo
     * @method get
     * @responseType ApplicationInfo
     */
    getApplicationInfo: '/info',
    /*
     * Gets the address of the authority that the host application uses for claim checks.
     * @tags ApplicationInfo
     * @method get
     * @responseType string
     */
    getAuthorityAddress: '/authority',
    // Correlations
    /*
     * Gets all Correlations.
     * @tags Correlations
     * @method get
     * @queryParams limit: number, offset: number, includeProcessInstances: boolean
     * @responseType CorrelationList
     */
    getCorrelations: '/correlations',
    /*
     * Gets a Correlation by its ID.
     * @tags Correlations
     * @method get
     * @queryParams includeProcessInstances: boolean
     * @responseType Correlation
     */
    getCorrelationById: `/correlations/${params.correlationId}`,
    // Cronjobs
    /*
     * Gets all Cronjobs.
     * @tags Cronjobs
     * @method get
     * @queryParams limit: number, offset: number
     * @responseType CronjobList
     */
    getActiveCronjobs: '/cronjobs',
    /*
     * Queries a list of cronjob history entries that match the given query.
     * @tags Cronjobs
     * @method get
     * @queryParams limit: number, offset: number: string, processDefinitionId: string, processModelId: string, startEventId: string, crontab: string, executedAt: string, createdAt: string, updatedAt: string
     * @responseType CronjobHistoryList
     */
    getCronjobHistory: '/cronjobs/history',
    // DataObjects
    /**
     * Gets the DataObjectInstances that match the given query.
     * @tags DataObjectInstances
     * @method get
     * @queryParams limit: number, offset: number: string, dataObjectId: string, processDefinitionId: string, processModelId: string, processInstanceId: string, flowNodeInstanceId: string, createdAt: string
     * @responseType DataObjectInstanceList
     */
    getDataObjects: '/data_object_instances/query',
    // FlowNodeInstance
    /*
     * Gets all FlowNodeInstances that match the given query.
     * @tags FlowNodeInstances
     * @method get
     * @queryParams limit: number, offset: number, flowNodeInstanceId: string, flowNodeId: string, flowNodeName: string, flowNodeLane: string, flowNodeType: string, eventType: string, correlationId: string, processDefinitionId: string, processModelId: string, processInstanceId: string, ownerId: string, state: string, previousFlowNodeInstanceId: string, parentProcessInstanceId: string, createdAt: string, updatedAt: string
     * @responseType FlowNodeInstanceList
     */
    queryFlowNodeInstances: '/flow_node_instances',
    /*
     * DEPRECATED - Use 'GET /flow_node_instances' instead.
     * @tags ActiveTokens
     * @method get
     * @deprecated
     * @queryParams limit: number, offset: number, processDefinitionId: string, processModelId: string, processInstanceId: string, flowNodeInstanceId: string, flowNodeId: string, correlationId: string
     * @responseType ActiveTokenList
     */
    queryActiveTokens: '/active_tokens',
    /*
     * Triggers a MessageEvent by its name.
     * @tags Events
     * @queryParams processInstanceId: string
     * @requestBody {"required": false, "bodyType": "application/json", "contentType": {"payload": {"type": "string"}}}
     * @method post
     */
    triggerMessageEvent: `/messages/${params.eventName}/trigger`,
    /*
     * Triggers a SignalEvent by its name.
     * @tags Events
     * @method post
     */
    triggerSignalEvent: `/signals/${params.eventName}/trigger`,
    /*
     * Finishes a specific EmptyActivity by its EmptyActivityInstanceId.
     * @tags EmptyActivities
     * @method put
     */
    finishEmptyActivity: `/empty_activities/${params.emptyActivityInstanceId}/finish`,
    /*
     * Finishes a specific ManualTask by its ManualTaskInstanceId.
     * @tags ManualTasks
     * @method put
     */
    finishManualTask: `/manual_tasks/${params.manualTaskInstanceId}/finish`,
    /*
     * Finishes a specific UserTask by its UserTaskInstanceId.
     * @tags UserTasks
     * @method put
     */
    finishUserTask: `/user_tasks/${params.userTaskInstanceId}/finish`,
    /*
     * Reserves a UserTask for a specific User.
     * @tags UserTasks
     * @method put
     * @requestBody {"required": true, "bodyType": "application/json", "contentType": {"actualOwnerId": {"type": "string", "required": true}}}
     */
    reserveUserTaskInstance: `/user_tasks/${params.userTaskInstanceId}/reserve`,
    /*
     * Cancels the reservation of the given UserTask instance.
     * @tags UserTasks
     * @method delete
     */
    cancelUserTaskInstanceReservation: `/user_tasks/${params.userTaskInstanceId}/cancel-reservation`,
    // External Tasks
    /*
     * Fetches a list of all ExternalTask topics from all deployed Process Models.
     * @tags ExternalTasks
     * @method get
     * @responseType Array<string>
     */
    getAllDeployedTopics: '/external_tasks/deployed_topics',
    /*
     * Fetches the tasks available for an ExternalTaskWorker and locks them for a defined time.
     * @tags ExternalTasks
     * @method post
     * @requestBody {"required": true, "bodyType": "application/json", "contentType": "FetchAndLockRequestPayload", "example": "{\"workerId\": \"string\", \"topicName\": \"string | Array<string>\", \"maxTasks\": 10, \"longPollingTimeout\": 2000, \"lockDuration\": 10000, \"payloadFilter\": \"string\"}"}
     * @responseType Array<ExternalTask>
     */
    fetchAndLockExternalTasks: '/external_tasks/fetch_and_lock',
    /*
     * Extends the lock duration of an ExternalTask by a given amount of time.
     * @tags ExternalTasks
     * @method put
     * @requestBody {"required": true, "bodyType": "application/json", "contentType": "ExtendLockRequestPayload"}
     */
    extendExternalTaskLock: `/external_tasks/${params.externalTaskId}/extend_lock`,
    /*
     * Reports a business error in the context of a running ExternalTask with a specific ID.
     * @tags ExternalTasks
     * @method put
     * @requestBody {"required": true, "bodyType": "application/json", "contentType": "HandleBpmnErrorRequestPayload"}
     */
    finishExternalTaskWithBpmnError: `/external_tasks/${params.externalTaskId}/handle_bpmn_error`,
    /*
     * Reports a failure to execute an ExternalTask with a specific ID.
     * @tags ExternalTasks
     * @method put
     * @requestBody {"required": true, "bodyType": "application/json", "contentType": "HandleServiceErrorRequestPayload"}
     */
    finishExternalTaskWithServiceError: `/external_tasks/${params.externalTaskId}/handle_service_error`,
    /*
     * Finishes the ExternalTask with the given ID.
     * @tags ExternalTasks
     * @method put
     * @requestBody {"required": true, "bodyType": "application/json", "contentType": "FinishExternalTaskRequestPayload"}
     */
    finishExternalTask: `/external_tasks/${params.externalTaskId}/finish`,
    // ProcessDefinition
    /*
     * Creates or updates a ProcessDefinition or a list of ProcessDefinitions.
     * @tags ProcessDefinitions
     * @method post
     * @requestBody {"required": true, "bodyType": "application/json", "contentType": "PersistProcessDefinitionsPayload", "example": "{\"xml\": \"string | Array<string>\", \"overrideExisting\": true}"}
     */
    persistProcessDefinitions: '/process_definitions',
    /*
     * Gets all ProcessDefinitions the requesting user is allowed to see.
     * @tags ProcessDefinitions
     * @method get
     * @queryParams offset: number, limit: number
     * @responseType ProcessDefinitionList
     */
    getProcessDefinitions: '/process_definitions',
    /*
     * Gets a ProcessDefinition by its id.
     * @tags ProcessDefinitions
     * @method get
     * @responseType ProcessDefinition
     */
    getProcessDefinitionById: `/process_definitions/${params.processDefinitionId}`,
    /*
     * Deletes the ProcessDefinition with the given ID.
     * Per default, a Process is only flagged as deleted. To actually delete the Process and all related data, set "delete_all_related_data" to "true".
     * @tags ProcessDefinitions
     * @method delete
     * @queryParams delete_all_related_data: boolean
     */
    deleteProcessDefinition: `/process_definitions/${params.processDefinitionId}`,
    // ProcessModels
    /*
     * Gets all currently deployed Process Models.
     * @tags ProcessModels
     * @method get
     * @responseType ProcessModelList
     */
    getAllProcessModels: '/process_models',
    /*
     * Gets a deployed Process Model by its ID.
     * @tags ProcessModels
     * @method get
     * @responseType ProcessModel
     */
    getProcessModelById: `/process_models/${params.processModelId}/`,
    /*
     * Gets a ProcessDefinition for a given ProcessModel.
     * @tags ProcessModels
     * @method get
     * @responseType ProcessDefinition
     */
    getProcessDefinitionForProcessModel: `/process_models/${params.processModelId}/process_definition`,
    /*
     * DEPRECATED - Gets all logs of a ProcessModel.
     * @tags ProcessModels
     * @method get
     * @deprecated
     * @queryParams correlationId: string, offset: number, limit: number
     * @responseType LogEntryList
     */
    getLogForProcessModel: `/process_models/${params.processModelId}/logs`,
    /*
     * Gets the RuntimeInformation for a specific ProcessModel.
     * @tags ProcessModels
     * @method get
     * @queryParams processModelId: string, correlationId: string, offset: number, limit: number
     * @responseType FlowNodeKpiList
     */
    getRuntimeInformationForProcessModel: `/process_models/${params.processModelId}/kpis`,
    /*
     * Starts a new instance from the given set of parameters.
     * @tags ProcessModels
     * @method post
     * @requestBody {"required": true, "bodyType": "application/json", "contentType": "ProcessStartRequest"}
     * @responseType ProcessStartResponse
     */
    startProcessInstance: `/process_models/${params.processModelId}/start`,
    /*
     * Deletes the ProcessDefinition that contains the Process Model with the given ID.
     * Per default, a Process is only flagged as deleted. To actually delete the Process and all related data, set "delete_all_related_data" to "true".
     * @tags ProcessModels
     * @method delete
     * @queryParams delete_all_related_data: boolean
     */
    deleteProcessDefinitionByProcessModelId: `/process_models/${params.processModelId}`,
    /*
     * Enables the given Process Model.
     * @tags ProcessModels
     * @method post
     */
    enableProcessModel: `/process_models/${params.processModelId}/enable`,
    /*
     * Disables the given Process Model.
     * @tags ProcessModels
     * @method post
     */
    disableProcessModel: `/process_models/${params.processModelId}/disable`,
    // ProcessInstance
    /*
     * Gets all ProcessInstances that match the given query.
     * @tags ProcessInstances
     * @method get
     * @queryParams offset: number, limit: number, includeXml: boolean, correlationId: Array<string> | string | SearchQuery, processInstanceId: Array<string> | string | SearchQuery, processDefinitionId: Array<string> | string | SearchQuery, processModelId: Array<string> | string | SearchQuery, processModelName: Array<string> | string | SearchQuery, processModelHash: Array<string> | string | SearchQuery, ownerId: Array<string> | string | SearchQuery, state: Array<string> | string | SearchQuery, parentProcessInstanceId: Array<string> | string | SearchQuery, terminatedByUserId: Array<string> | string | SearchQuery, createdBefore: Date, createdAt: Array<Date> | Date, createdAfter: Date, updatedBefore: Date, updatedAt: Array<Date> | Date, updatedAfter: Date, finishedBefore: Date, finishedAt: Array<Date> | Date, finishedAfter: Date
     * @responseType ProcessInstanceList
     */
    queryProcessInstances: '/process_instances/query',
    /*
     * Gets the ProcessDefinition for a given ProcessInstance.
     * @tags ProcessInstances
     * @method get
     * @responseType ProcessDefinition
     */
    getProcessDefinitionForProcessInstance: `/process_instances/${params.processInstanceId}/process_definition`,
    /*
     * Gets the ProcessDefinition for a given ProcessInstance.
     * @tags ProcessInstances
     * @method get
     * @responseType ProcessDefinition
     */
    getProcessModelForProcessInstance: `/process_instances/${params.processInstanceId}/process_model`,
    /*
     * DEPRECATED - Gets the log of a specific ProcessInstance.
     * @tags ProcessInstances
     * @deprecated
     * @method get
     * @queryParams offset: number, limit: number
     * @responseType LogEntryList
     */
    getLogForProcessInstance: `/process_instances/${params.processInstanceId}/logs`,
    /*
     * Terminates the ProcessInstance with the given ID.
     * @tags ProcessInstances
     * @method put
     */
    terminateProcessInstances: `/process_instances/${params.processInstanceId}/terminate`,
    /*
     * Restarts a ProcessInstance that finished with an error.
     * @tags ProcessInstances
     * @method put
     */
    retryProcessInstance: `/process_instances/${params.processInstanceId}/retry`,
    /*
     * Deletes the given set of Process Instances. Multiple ProcessInstanceIds can be provided with ";" as separator.
     * Per default, a Process Instance is only flagged as deleted. To actually delete the Process Instances and all related data, set "delete_all_related_data" to "true".
     * @tags ProcessInstances
     * @method delete
     * @queryParams process_instance_ids: string | Array<string>, delete_all_related_data: boolean
     */
    deleteProcessInstances: `/process_instances`,
    // Long Polling Notifications
    /*
     * Wait for a CronjobCreated notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType CronjobEventMessage
     */
    onCronjobCreated: '/notifications/long_polling/cronjob_created',
    /*
     * Wait for a CronjobExecuted notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType CronjobEventMessage
     */
    onCronjobExecuted: '/notifications/long_polling/cronjob_executed',
    /*
     * Wait for a CronjobStopped notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType CronjobEventMessage
     */
    onCronjobStopped: '/notifications/long_polling/cronjob_stopped',
    /*
     * Wait for a CronjobUpdated notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType CronjobEventMessage
     */
    onCronjobUpdated: '/notifications/long_polling/cronjob_updated',
    /*
     * Wait for a CronjobRemoved notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType CronjobEventMessage
     */
    onCronjobRemoved: '/notifications/long_polling/cronjob_removed',
    /*
     * Wait for a ProcessDeployed notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType ProcessDeploymentMessage
     */
    onProcessDeployed: '/notifications/long_polling/process_deployed',
    /*
     * Wait for a ProcessIsExecutableChanged notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType ProcessIsExecutableChangedMessage
     */
    onProcessIsExecutableChanged: '/notifications/long_polling/process_is_executable_changed',
    /*
     * Wait for a ProcessUndeployed notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType ProcessDeploymentMessage
     */
    onProcessUndeployed: '/notifications/long_polling/process_undeployed',
    /*
     * Wait for a ProcessStarting notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onProcessStarting: '/notifications/long_polling/process_starting',
    /*
     * Wait for a ProcessStarted notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onProcessStarted: '/notifications/long_polling/process_started',
    /*
     * Wait for a ProcessResumed notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onProcessResumed: '/notifications/long_polling/process_resumed',
    /*
     * Wait for a ProcessEnded notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onProcessEnded: '/notifications/long_polling/process_ended',
    /*
     * Wait for a ProcessTerminated notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onProcessTerminated: '/notifications/long_polling/process_terminated',
    /*
     * Wait for a ProcessError notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onProcessError: '/notifications/long_polling/process_error',
    /*
     * Wait for a ProcessInstancesDeleted notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onProcessInstancesDeleted: '/notifications/long_polling/process_instances_deleted',
    /*
     * Wait for an ExternalTaskCreated notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType ExternalTaskCreatedMessage
     */
    onExternalTaskCreated: '/notifications/long_polling/external_task_created',
    /*
     * Wait for a ExternalTaskLocked notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType ExternalTaskLockedMessage
     */
    onExternalTaskLocked: '/notifications/long_polling/external_task_locked',
    /*
     * Wait for a ExternalTaskUnlocked notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType ExternalTaskUnlockedMessage
     */
    onExternalTaskUnlocked: '/notifications/long_polling/external_task_unlocked',
    /*
     * Wait for an ActivityReached notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onActivityReached: '/notifications/long_polling/activity_reached',
    /*
     * Wait for an ActivityFinished notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onActivityFinished: '/notifications/long_polling/activity_finished',
    /*
     * Wait for an EmptyActivityWaiting notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType ActivityReachedMessage
     */
    onEmptyActivityWaiting: '/notifications/long_polling/empty_activity_waiting',
    /*
     * Wait for an EmptyActivityFinished notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType ActivityFinishedMessage
     */
    onEmptyActivityFinished: '/notifications/long_polling/empty_activity_finished',
    /*
     * Wait for a ManualTaskWaiting notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onManualTaskWaiting: '/notifications/long_polling/manual_task_waiting',
    /*
     * Wait for a ManualTaskFinied notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onManualTaskFinished: '/notifications/long_polling/manual_task_finished',
    /*
     * Wait for a UserTaskWaiting notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onUserTaskWaiting: '/notifications/long_polling/user_task_waiting',
    /*
     * Wait for a UserTaskFinished notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onUserTaskFinished: '/notifications/long_polling/user_task_finished',
    /*
     * Wait for a UserTaskReserved notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onUserTaskReserved: '/notifications/long_polling/user_task_reserved',
    /*
     * Wait for a UserTaskReservationCanceled notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onUserTaskReservationCanceled: '/notifications/long_polling/user_task_reservation_canceled',
    /*
     * Wait for a BoundaryEventTriggered notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onBoundaryEventTriggered: '/notifications/long_polling/boundary_event_triggered',
    /*
     * Wait for an IntermediateThrowEventTriggered notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onIntermediateThrowEventTriggered: '/notifications/long_polling/intermediate_throw_event_triggered',
    /*
     * Wait for an IntermediateCatchEventReached notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onIntermediateCatchEventReached: '/notifications/long_polling/intermediate_catch_event_reached',
    /*
     *
     * Wait for a IntermediateCatchEventFinished notification and resolves with the notification's content.
     * @tags Notifications
     * @method get
     * @responseType EventMessage
     */
    onIntermediateCatchEventFinished: '/notifications/long_polling/intermediate_catch_event_finished',
};
exports.restSettings = {
    params: params,
    paths: paths,
    baseRoute: 'atlas_engine/api',
};
//# sourceMappingURL=RestSettings.js.map